import React, { useState } from 'react';
import {
  Home,
  Gift,
  QrCode,
  Clock,
  Settings,
  CheckCircle,
  Plus,
  Edit,
  Camera,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KPICard, StatsMiniCard } from '@/components/cards/LoyaltyCards';
import { merchantData, merchantOffers, merchantRedemptionHistory } from '@/data/mockData';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

// Partner Home
export function PartnerHome() {
  const { setActiveTab } = useApp();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-primary-foreground font-bold">F</span>
            </div>
            <div>
              <h1 className="font-display font-semibold">{merchantData.partnerName}</h1>
              <p className="text-xs text-muted-foreground">{merchantData.branchName}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            <Settings className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <KPICard
            title="Today's Redemptions"
            value={merchantData.todayRedemptions.toString()}
            icon={Gift}
            variant="primary"
          />
          <KPICard
            title="Pending Settlement"
            value={`RM ${merchantData.pendingSettlement.toLocaleString()}`}
            icon={Clock}
          />
        </div>

        {/* Scan Button */}
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="p-6 text-center">
            <Button
              size="xl"
              variant="secondary"
              className="w-full h-16 text-lg"
              onClick={() => setActiveTab('scan')}
            >
              <QrCode className="h-6 w-6 mr-3" />
              Scan Voucher
            </Button>
            <p className="text-sm text-primary-foreground/70 mt-3">
              Scan customer&apos;s QR code to validate voucher
            </p>
          </CardContent>
        </Card>

        {/* Active Offers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Your Active Offers
              <Badge variant="secondary">{merchantData.activeOffers}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {merchantOffers.map((offer) => (
              <div
                key={offer.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
              >
                <div className="flex-1">
                  <p className="font-medium">{offer.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {offer.used} / {offer.quantity} redeemed
                  </p>
                </div>
                <Badge variant={offer.status === 'active' ? 'active' : 'secondary'}>
                  {offer.status}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Redemptions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Redemptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {merchantRedemptionHistory.slice(0, 3).map((redemption) => (
              <div
                key={redemption.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-success" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{redemption.customerName}</p>
                    <p className="text-xs text-muted-foreground">{redemption.offerTitle}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(redemption.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Nav */}
      <PartnerBottomNav />
    </div>
  );
}

// Voucher Scan Page
export function PartnerScan() {
  const { addNotification, setActiveTab } = useApp();
  const [voucherCode, setVoucherCode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleValidate = () => {
    if (!voucherCode) return;

    setIsValidating(true);
    // Simulate validation
    setTimeout(() => {
      setIsValidating(false);
      if (voucherCode.toUpperCase().includes('PHYSIO')) {
        setScanResult({
          valid: true,
          voucher: {
            code: voucherCode.toUpperCase(),
            title: 'RM20 Gym Day Pass',
            customer: 'Sarah C.',
            expiryDate: '2024-04-08',
          },
        });
      } else {
        setScanResult({
          valid: false,
          error: 'Invalid voucher code',
        });
      }
    }, 1500);
  };

  const handleMarkRedeemed = () => {
    addNotification({ type: 'success', message: 'Voucher marked as redeemed!' });
    setScanResult(null);
    setVoucherCode('');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-4">
          <h1 className="font-display font-semibold text-lg">Validate Voucher</h1>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('home')}>
            Cancel
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* QR Scanner Placeholder */}
        <Card>
          <CardContent className="p-6">
            <div className="aspect-square max-w-xs mx-auto bg-muted rounded-xl flex flex-col items-center justify-center mb-4">
              <Camera className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">QR Scanner</p>
              <p className="text-xs text-muted-foreground text-center mt-1">
                Camera access required
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or enter code</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <Input
                placeholder="Enter voucher code"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value)}
                className="text-center font-mono uppercase"
              />
              <Button
                className="w-full"
                onClick={handleValidate}
                disabled={!voucherCode || isValidating}
              >
                {isValidating ? 'Validating...' : 'Validate Code'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Scan Result */}
        {scanResult && (
          <Card className={cn(scanResult.valid ? 'border-success' : 'border-destructive')}>
            <CardContent className="p-6 text-center">
              {scanResult.valid ? (
                <>
                  <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                  <h2 className="font-display font-bold text-xl text-success mb-2">
                    Valid Voucher
                  </h2>
                  <div className="bg-muted rounded-lg p-4 mb-4 text-left">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Code</span>
                        <span className="font-mono font-medium">{scanResult.voucher.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Offer</span>
                        <span className="font-medium">{scanResult.voucher.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer</span>
                        <span className="font-medium">{scanResult.voucher.customer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expires</span>
                        <span className="font-medium">{scanResult.voucher.expiryDate}</span>
                      </div>
                    </div>
                  </div>
                  <Button className="w-full" size="lg" onClick={handleMarkRedeemed}>
                    Mark as Redeemed
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-3xl">!</span>
                  </div>
                  <h2 className="font-display font-bold text-xl text-destructive mb-2">
                    Invalid Voucher
                  </h2>
                  <p className="text-muted-foreground">{scanResult.error}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setScanResult(null);
                      setVoucherCode('');
                    }}
                  >
                    Try Again
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <PartnerBottomNav />
    </div>
  );
}

// Partner Offers Management
export function PartnerOffers() {
  const { addNotification } = useApp();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-4">
          <h1 className="font-display font-semibold text-lg">My Offers</h1>
          <Button size="sm" onClick={() => addNotification({ type: 'info', message: 'Create offer coming soon' })}>
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {merchantOffers.map((offer) => (
          <Card key={offer.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold">{offer.title}</h3>
                  <Badge variant="points" className="mt-1">
                    {offer.pointsCost.toLocaleString()} pts
                  </Badge>
                </div>
                <Badge variant={offer.status === 'active' ? 'active' : 'secondary'}>
                  {offer.status}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quantity</span>
                  <span>
                    {offer.used} / {offer.quantity} used
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${(offer.used / offer.quantity) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valid</span>
                  <span>
                    {offer.validFrom} - {offer.validTo}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm">
                  View Stats
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PartnerBottomNav />
    </div>
  );
}

// Partner History
export function PartnerHistory() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-4">
          <h1 className="font-display font-semibold text-lg">Redemption History</h1>
        </div>
      </header>

      <div className="p-4">
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Offer</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {merchantRedemptionHistory.map((redemption) => (
                  <TableRow key={redemption.id}>
                    <TableCell className="font-medium">{redemption.customerName}</TableCell>
                    <TableCell className="text-sm">{redemption.offerTitle}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(redemption.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="success">{redemption.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <PartnerBottomNav />
    </div>
  );
}

// Partner Bottom Navigation
function PartnerBottomNav() {
  const { activeTab, setActiveTab } = useApp();

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'scan', label: 'Scan', icon: QrCode },
    { id: 'offers', label: 'Offers', icon: Gift },
    { id: 'history', label: 'History', icon: Clock },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50">
      <div className="flex items-center justify-around py-2 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={cn(
                'flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors min-w-[60px]',
                isActive
                  ? 'text-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className={cn('h-5 w-5 mb-1', isActive && 'text-primary')} />
              <span className={cn('text-xs font-medium', isActive && 'text-primary')}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Partner Layout
export function PartnerLayout() {
  const { activeTab } = useApp();

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <PartnerHome />;
      case 'scan':
        return <PartnerScan />;
      case 'offers':
        return <PartnerOffers />;
      case 'history':
        return <PartnerHistory />;
      default:
        return <PartnerHome />;
    }
  };

  return renderContent();
}
